import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { getClientes } from '@spark/services';
import { tomadorFilterConfig } from '../config/tomadorFilterConfig';

const TomadorSelectionModal = ({ show, onHide, onSelect }) => {
  const formatCnpj = (value) => {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (digits.length !== 14) return value || '';
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);

  useEffect(() => {
    if (!show) {
      setFiltros({});
      setPaginaAtual(1);
      setQuantidadePorPagina(10);
      return;
    }
    setFiltros({});
    setPaginaAtual(1);
    setQuantidadePorPagina(10);
  }, [show]);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    const sanitized = { ...(novosFiltros || {}) };
    if (sanitized.Nome) {
      sanitized.Nome = String(sanitized.Nome).trim();
    }
    if (sanitized.CNPJ) {
      sanitized.CNPJ = String(sanitized.CNPJ).replace(/\D/g, '');
    }
    setFiltros(sanitized);
    setPaginaAtual(1);
  }, []);

  const carregarClientes = useCallback(async () => {
    if (!show) return;
    setLoading(true);
    try {
      const searchTerm = filtros?.CNPJ || filtros?.Nome || '';
      const response = await getClientes({
        pagina: paginaAtual,
        quantidadePorPagina,
        page: paginaAtual,
        limit: quantidadePorPagina,
        ...(searchTerm ? { search: searchTerm } : {})
      });
      const registros = Array.isArray(response)
        ? response
        : response?.registros || response?.data || [];
      setClientes(registros);
      setTotalRegistros(
        response?.totalRegistros ??
        response?.total ??
        response?.totalItems ??
        registros.length
      );
    } catch (error) {
      console.error('Erro ao carregar tomadores:', error);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  }, [show, filtros, paginaAtual, quantidadePorPagina]);

  const handlePaginationChange = useCallback((paginationState) => {
    const nextPage = (paginationState.pageIndex ?? 0) + 1;
    const nextSize = paginationState.pageSize ?? quantidadePorPagina;
    if (nextSize !== quantidadePorPagina) {
      setQuantidadePorPagina(nextSize);
      setPaginaAtual(1);
      return;
    }
    if (nextPage !== paginaAtual) {
      setPaginaAtual(nextPage);
    }
  }, [paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);

  const handleSelect = (cliente) => {
    const nome = cliente.Nome ?? cliente.nome ?? '';
    const cnpj = cliente.CNPJ ?? cliente.Cnpj ?? cliente.cnpj ?? '';
    const id = cliente.Id ?? cliente.id ?? null;
    const label = nome && cnpj ? `${nome} - ${cnpj}` : nome || cnpj || `Cliente ${id ?? ''}`;
    onSelect({
      value: String(id ?? ''),
      label,
      cnpj
    });
    onHide();
  };

  const columns = useMemo(() => [
    {
      header: 'Nome',
      accessorKey: 'Nome',
      cell: ({ row }) => row.original.Nome ?? row.original.nome ?? '-'
    },
    {
      header: 'CNPJ',
      accessorKey: 'CNPJ',
      cell: ({ row }) => {
        const raw = row.original.CNPJ ?? row.original.Cnpj ?? row.original.cnpj ?? '';
        return formatCnpj(raw) || '-';
      }
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: ({ row }) => (
        <Button variant="primary" size="sm" onClick={() => handleSelect(row.original)}>
          Selecionar
        </Button>
      )
    }
  ], []);

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Selecionar Tomador</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={tomadorFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`tomador-selection-${paginaAtual}-${quantidadePorPagina}`}
          columns={columns}
          data={clientes}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {totalRegistros > 0 && (
              <span>
                Exibindo {clientes.length} de {totalRegistros} tomador(es)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TomadorSelectionModal;
